import { withCompInfo } from '@wix/editor-elements-integrations'
import { BackgroundMedia, PageData, PageDefinition } from '@wix/thunderbolt-becky-types'
import { getFillLayersCSSVars } from '../../core/mappers/FillLayers/FillLayers.utils'
import { PageBackgroundCssVars } from './PageBackground'
import { isImageMedia, isVideoMedia } from '../../core/mappers/Media/Media.utils'
import _ from 'lodash'

const STORES_DEF_ID = '1380b703-ce81-ff05-f115-39571d94dfcd'

const getMobileBackgroundMediaSizing = (compData: PageData) => {
	return _.get(compData, ['pageBackgrounds', 'mobile', 'mediaSizing'], null)
}

const getScrollType = (compData: PageData, backgroundMedia: BackgroundMedia, isMobileView: boolean) => {
	const backgroundMediaSizing = getMobileBackgroundMediaSizing(compData)
	const isViewportMediaSizing = backgroundMediaSizing === 'viewport'
	const hasMedia = isVideoMedia(backgroundMedia) || isImageMedia(backgroundMedia)
	return !hasMedia || (isMobileView && !isViewportMediaSizing) ? 'scroll' : backgroundMedia.scrollType
}

export const getBackgroundMedia = (compData: PageData, isMobileView: boolean): BackgroundMedia => {
	const viewMode = isMobileView ? 'mobile' : 'desktop'
	const backgroundMediaForViewMode = _.get(compData, ['pageBackgrounds', viewMode, 'ref'])
	return backgroundMediaForViewMode
		? { ...backgroundMediaForViewMode, showOverlayForMediaType: 'all' }
		: ({} as BackgroundMedia)
}

export const backgroundHasParallax = (compData: PageData, isMobileView: boolean) => {
	const backgroundData = getBackgroundMedia(compData, isMobileView)
	const scrollType = backgroundData?.scrollType || 'scroll'
	const hasImageMedia = isImageMedia(backgroundData)
	return scrollType === 'parallax' && hasImageMedia
}

const isStoresCheckoutPage = (compData: PageData) => {
	if (!compData) {
		return false
	}
	const tpaPageId = compData.tpaPageId
	const tpaDefId = compData.managingAppDefId
	return tpaDefId === STORES_DEF_ID && tpaPageId === 'checkout'
}

export const css = withCompInfo<PageBackgroundCssVars, PageDefinition>()(
	[
		'compData',
		'compDesign',
		'compProps',
		'experiments',
		'isMobileView',
		'siteColors',
		'viewMode',
		'compId',
		'hasResponsiveLayout',
		// @ts-ignore - Remove once animations is added to the compInfo
		'animations',
		// @ts-ignore - Remove once isNonAdaptiveMobile is added to the compInfo
		'isNonAdaptiveMobile',
	],
	({
		compData,
		compDesign,
		compProps,
		experiments,
		isMobileView,
		siteColors,
		viewMode,
		compId,
		hasResponsiveLayout,
		// @ts-ignore - Remove once animations is added to the compInfo
		animations,
		// @ts-ignore - Remove once isNonAdaptiveMobile is added to the compInfo
		isNonAdaptiveMobile,
	}) => {
		if (hasResponsiveLayout || compData.isPopup) {
			return {}
		}

		const backgroundMedia = getBackgroundMedia(compData, isMobileView)
		const isVideo = isVideoMedia(backgroundMedia)
		const scrollType = getScrollType(compData, backgroundMedia, isMobileView)
		const isParallax = scrollType === 'parallax'
		const isFixed = scrollType === 'fixed'

		const imageHeight = {
			'--fill-layer-image-height': backgroundHasParallax(compData, isMobileView) && ('120%' as const),
		}

		const fillLayersCSSVars = _.assignIn(
			imageHeight,
			getFillLayersCSSVars({
				animations,
				backgroundMedia,
				compDesign,
				compProps,
				compId,
				experiments,
				isMobileView,
				isNonAdaptiveMobile,
				siteColors,
				viewMode,
			})
		)

		const pageBackgroundCSSVars = {
			'--bg-position': isVideo || isFixed || isParallax ? ('fixed' as const) : ('absolute' as const),
		}

		const finalCss = isStoresCheckoutPage(compData)
			? {
				'--bg-overlay-color': 'rgba(255,255,255,1)',
				'--bg-position': 'absolute',
			}
			: { ...pageBackgroundCSSVars, ...fillLayersCSSVars }

		return _.pickBy(finalCss, (v) => _.isNumber(v) || v)
	}
)
